import { Link } from 'react-router-dom';
import type { DeviceMix, Presence, Seat } from '@/api/types';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { StatTile } from '@/components/StatTile';
import { formatWhen } from '@/pages/AccountsPage';

/**
 * The shared vocabulary for "is this person here, and is this seat alive".
 *
 * Kept in one file because presence is rendered in three places — the account strip, the people
 * table and the per-user page — and three copies of "what does live mean" is how they drift apart.
 */

/**
 * A seat's live state as a dot AND the words beside it.
 *
 * Colour never carries the state alone: `styles.css` states that rule for the connection dot on the
 * Live page, and it holds harder here because these dots sit in a dense table where a red-green
 * distinction is the only thing separating two rows.
 *
 * The dot pulses ONLY when live. The always-animating `.live-dot` used by the realtime card means
 * "this panel updates itself"; a row that pulses whether or not anybody is signed in says nothing.
 */
export function LiveDot({ live, label }: { live: boolean; label?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className={`live-dot ${live ? 'is-live' : 'is-stale'}`} aria-hidden="true" />
      <span className={live ? undefined : 'subtle'}>{label ?? (live ? 'Live' : 'Offline')}</span>
    </span>
  );
}

/**
 * When the replica was last refreshed.
 *
 * Rendered next to every presence number, never optional. These come from a store the collector
 * refreshes every few hours, so "online now" is really "was live as of this timestamp" — and shown
 * bare, a three-hour-old count is indistinguishable from a live one.
 */
export function SyncNote({ syncedAt }: { syncedAt: string | null }) {
  if (!syncedAt) return <span className="t-label subtle">never synced</span>;

  return (
    <span className="t-label subtle">
      as of {formatWhen(syncedAt)} · not a live read
    </span>
  );
}

/** active | dormant | never_signed_in, as a chip with the reserved status tones. */
export function SeatStateChip({ state }: { state: string }) {
  if (state === 'active') return <span className="chip chip-on">active</span>;

  // Never signed in and gone quiet are different findings: one is a rollout that failed to start,
  // the other one that stopped. The people table already styles them differently; matching it here
  // keeps the two surfaces telling the same story.
  if (state === 'never_signed_in') return <span className="chip chip-down">never signed in</span>;

  return <span className="chip chip-off">dormant</span>;
}

/**
 * The four numbers that answer "is this account alive", with their denominator.
 *
 * `sessions_collected === false` replaces the numbers with a stated absence rather than zeros.
 * Zeros here would be a claim about the customer — every seat dead — when the truth is a claim
 * about the collector, and somebody would act on the first one.
 */
export function PresenceStrip({ presence }: { presence: Presence }) {
  if (!presence.sessions_collected) {
    // The shared Callout, not hand-rolled markup. The first version of this used a raw
    // `<div className="callout callout-warning">` and rendered with different chrome from every
    // other warning in the product, plus a code-styled leading space. Found by screenshot.
    return (
      <Callout tone="warning" title="No session data for this account">
        Presence and seat health are unknown, not zero — the collector has not landed this tenant's
        login history yet. The people list below is unaffected.
      </Callout>
    );
  }

  const utilization = presence.seat_utilization;

  return (
    <>
      <div className="kpi-row">
        <StatTile
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                className={`live-dot ${presence.online_now > 0 ? 'is-live' : 'is-stale'}`}
                aria-hidden="true"
              />
              Online now
            </span>
          }
          value={presence.online_now}
          sub={`of ${presence.total_seats} seats`}
        />
        <StatTile
          label="Active today"
          value={presence.active_today}
          sub="since 00:00 UTC"
        />
        <StatTile
          label="Dead seats"
          value={presence.dead_seats}
          tone={presence.dead_seats > 0 ? 'warning' : 'default'}
          sub={
            presence.never_signed_in > 0
              ? `${presence.never_signed_in} never signed in`
              : 'no login in 30 days'
          }
        />
        <StatTile
          label="Seat utilization"
          /* Null, not 0%: a customer with no seat cap has no utilization, and 0% would read as an
             unused licence. The em dash is the honest rendering of "there is no denominator". */
          value={utilization === null ? null : `${Math.round(utilization * 100)}%`}
          sub={presence.seat_limit === null ? 'no seat limit set' : `${presence.active_seats} of ${presence.seat_limit} licensed`}
        />
      </div>
      <div style={{ marginTop: -8 }}>
        <SyncNote syncedAt={presence.synced_at} />
      </div>
    </>
  );
}

/**
 * What people sign in from: device type, browser, OS, access type.
 *
 * Four bar lists in ONE hue rather than a pie or a coloured donut. Every list is the same measure —
 * sessions — sliced a different way, so a per-row colour would imply an identity that is not there;
 * and the sequential ramp in `viz.ts` is illegal on these because a browser is a nominal category,
 * not an ordered one. `BarList` already encodes exactly that rule.
 */
export function DeviceMixPanel({ mix, title }: { mix: DeviceMix; title: string }) {
  const KINDS: { kind: string; label: string }[] = [
    { kind: 'device_type', label: 'Device' },
    { kind: 'browser', label: 'Browser' },
    { kind: 'os', label: 'Operating system' },
    { kind: 'access_type', label: 'Access type' },
  ];

  if (mix.total_sessions === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        </div>
        <div className="empty-state t-body">No sessions replicated yet.</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <span className="t-label subtle">
          {mix.total_sessions.toLocaleString('en-US')} sessions
          {mix.tenants_in_scope !== null && ` · ${mix.tenants_in_scope} accounts`}
        </span>
      </div>

      <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {KINDS.map(({ kind, label }) => {
          const rows = mix.slices
            .filter((s) => s.kind === kind)
            .map((s) => ({ label: s.value, value: s.sessions }));

          const classified = rows.reduce((sum, r) => sum + r.value, 0);
          const unclassified = mix.total_sessions - classified;

          return (
            <div key={kind}>
              <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>{label}</h3>
              <BarList rows={rows} limit={6} emptyLabel="Not collected." />

              {/* The slices drop unreadable values instead of drawing a bar labelled nothing, so
                  they can sum to less than the total. Stating the gap keeps it visible — hidden,
                  it reads as a mix that accounts for every session. */}
              {unclassified > 0 && (
                <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
                  {unclassified.toLocaleString('en-US')} session(s) not classified
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Dead seats, worst first — the list somebody acts on. */
export function DeadSeatsPanel({ seats, tenantId }: { seats: Seat[]; tenantId: string }) {
  const dead = seats.filter((s) => s.state !== 'active');

  if (dead.length === 0) {
    return null;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Dead seats</h2>
        <span className="t-label subtle">no login in 30 days, or none ever</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Person</th>
            <th>State</th>
            <th>Last login</th>
            <th className="num">Days idle</th>
          </tr>
        </thead>
        <tbody>
          {dead.map((s) => (
            <tr key={s.user_id}>
              <td>
                <Link className="link" to={`/accounts/${tenantId}/users/${s.user_id}`}>
                  {s.display_name || <span className="subtle">(no name)</span>}
                </Link>
                <div className="t-caption muted">{s.email || '—'}</div>
              </td>
              <td><SeatStateChip state={s.state} /></td>
              <td className="muted">
                {s.last_login_at
                  ? formatWhen(s.last_login_at)
                  : <span style={{ color: 'var(--warning)' }}>never</span>}
              </td>
              {/* Never signed in has no "days idle" — an unused seat has no elapsed duration, and a
                  large number there would imply a login that never happened. */}
              <td className="num">{s.days_since_login ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
